import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Box, Flex, HStack, VStack, Text } from '@chakra-ui/react';
import { AccountContext } from '../../../../AccountContext';
import BottomNavBar from '../../../../BottomNavBar';
import ChildHeader from '../../ChildHeader';
import Loading from 'components/Loading';
import {
  FiArrowDownLeft,
  FiClock,
  FiPercent,
  FiDollarSign,
  FiInfo,
  FiTrendingUp,
  FiSliders,
  FiChevronDown,
} from 'react-icons/fi';
import { api } from 'api/api';

const ENDPOINT =
  process.env.REACT_APP_ENVIRONMENT === 'production'
    ? process.env.REACT_APP_BASE_URL_API_SERVER_PROD
    : process.env.REACT_APP_BASE_URL_API_SERVER_TEST;

function formatBRL(v) {
  return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function isPago(item) {
  return item.pago === true || item.pago === 'true';
}

function isVencida(item) {
  return item.vencida === true || item.vencida === 'true';
}

function statusItem(item) {
  if (isPago(item)) return 'pago';
  if (isVencida(item)) return 'vencido';
  return 'aguardando';
}

function filterByPeriod(lista, periodo) {
  const hoje = new Date();
  return lista.filter((item) => {
    const d = new Date(item.data_cadastro);
    if (periodo === 'mes_atual') {
      return d.getMonth() === hoje.getMonth() && d.getFullYear() === hoje.getFullYear();
    }
    if (periodo === 'mes_anterior') {
      const ref = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
      return d.getMonth() === ref.getMonth() && d.getFullYear() === ref.getFullYear();
    }
    return true;
  });
}

function mesLabel(periodo) {
  const hoje = new Date();
  if (periodo === 'mes_anterior') {
    return new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)
      .toLocaleDateString('pt-BR', { month: 'long' });
  }
  return hoje.toLocaleDateString('pt-BR', { month: 'long' });
}

function getDateKey(dateStr) {
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

function getDateLabel(dateStr) {
  const d = new Date(dateStr);
  const hoje = new Date();
  const ontem = new Date();
  ontem.setDate(ontem.getDate() - 1);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const dia = d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  if (sameDay(d, hoje)) return `Hoje • ${dia}`;
  if (sameDay(d, ontem)) return `Ontem • ${dia}`;
  return dia;
}

function agruparPorDia(lista) {
  const grupos = [];
  lista.forEach((item) => {
    const key = getDateKey(item.data_cadastro);
    let g = grupos.find((gr) => gr.key === key);
    if (!g) {
      g = { key, label: getDateLabel(item.data_cadastro), items: [] };
      grupos.push(g);
    }
    g.items.push(item);
  });
  return grupos;
}

function calcBars(lista) {
  // Weekly bars: S1 S2 S3 S4 Agora (current week)
  const semanas = [0, 0, 0, 0, 0];
  lista.filter(isPago).forEach((item) => {
    const day = new Date(item.data_cadastro).getDate();
    const idx = Math.min(Math.floor((day - 1) / 7), 4);
    semanas[idx] += Number(item.valor || 0);
  });
  const max = Math.max(...semanas, 1);
  return semanas.map((v, i) => ({
    height: Math.max(Math.round((v / max) * 48), 6),
    opacity: i === 4 ? 1 : 0.55,
    label: i === 4 ? 'Agora' : `S${i + 1}`,
    isActive: i === 4,
  }));
}

function MiniChart({ bars }) {
  return (
    <HStack spacing="6px" alignItems="flex-end" w="100%" h="64px">
      {bars.map((bar, i) => (
        <VStack key={i} spacing="4px" flex={1} alignItems="center" justifyContent="flex-end" h="100%">
          <Box
            w="100%"
            borderRadius="4px"
            bg={`rgba(255,255,255,${bar.opacity})`}
            style={{ height: `${bar.height}px` }}
          />
          <Text
            fontSize="9px"
            fontWeight={bar.isActive ? '700' : '500'}
            color={bar.isActive ? 'white' : 'rgba(255,255,255,0.8)'}
          >
            {bar.label}
          </Text>
        </VStack>
      ))}
    </HStack>
  );
}

function SummaryRow({ icon: Icon, iconColor, iconBg, title, subtitle, value, valueColor, bold, valueSize }) {
  return (
    <HStack spacing="12px" alignItems="center" w="100%">
      <Flex
        w="40px" h="40px" minW="40px"
        bg={iconBg} borderRadius="12px"
        alignItems="center" justifyContent="center"
      >
        <Icon color={iconColor} size={20} />
      </Flex>
      <VStack spacing="1px" alignItems="flex-start" flex={1} minW={0}>
        <Text color="#0F1B2A" fontSize="13px" fontWeight={bold ? '700' : '600'} noOfLines={1}>{title}</Text>
        <Text color="#5B6B7A" fontSize="11px" fontWeight="500" noOfLines={2} lineHeight="1.3">{subtitle}</Text>
      </VStack>
      <Text
        color={valueColor}
        fontSize={valueSize || '15px'}
        fontWeight="700"
        flexShrink={0}
        textAlign="right"
      >
        {value}
      </Text>
    </HStack>
  );
}

const TX_CFG = {
  pago: {
    icon: FiArrowDownLeft,
    iconColor: '#16A34A',
    iconBg: '#DCFCE7',
    title: 'Transferência realizada',
    valPrefix: '+',
    valColor: '#16A34A',
  },
  aguardando: {
    icon: FiClock,
    iconColor: '#D97706',
    iconBg: '#FEF3C7',
    title: 'Transferência agendada',
    valPrefix: '',
    valColor: '#D97706',
  },
  vencido: {
    icon: FiPercent,
    iconColor: '#EA580C',
    iconBg: '#FFEDD5',
    title: 'Pgto não realizado',
    valPrefix: '',
    valColor: '#EA580C',
  },
};

function TransactionItem({ item, isLast }) {
  const status = statusItem(item);
  const cfg = TX_CFG[status];
  const Icon = cfg.icon;
  const hora = new Date(item.data_cadastro).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const formaMap = { pix: 'Pix', boleto: 'Boleto', credit_card: 'Cartão', debit_card: 'Débito' };
  const forma = formaMap[item.forma_pagamento] || '';

  return (
    <>
      <HStack spacing="12px" px="10px" py="12px" w="100%" alignItems="center">
        <Flex
          w="40px" h="40px" minW="40px"
          bg={cfg.iconBg} borderRadius="12px"
          alignItems="center" justifyContent="center"
        >
          <Icon color={cfg.iconColor} size={20} />
        </Flex>

        <VStack spacing="2px" alignItems="flex-start" flex={1} minW={0}>
          <Text color="#0F1B2A" fontSize="13px" fontWeight="600" noOfLines={1}>
            {cfg.title}
          </Text>
          <Text color="#5B6B7A" fontSize="11px" fontWeight="500" noOfLines={1}>
            {item.plano || 'Plano'}
            {forma ? ` • ${forma}` : ''}
            {item.cliente ? ` • ${item.cliente}` : ''}
          </Text>
        </VStack>

        <VStack spacing="2px" alignItems="flex-end">
          <Text color={cfg.valColor} fontSize="13px" fontWeight="700">
            {cfg.valPrefix}R$ {formatBRL(item.valor)}
          </Text>
          <Text color="#9AA5B1" fontSize="10px" fontWeight="500">{hora}</Text>
        </VStack>
      </HStack>
      {!isLast && <Box h="1px" bg="#EEF1F5" />}
    </>
  );
}

const PERIODOS = [
  { value: 'mes_atual',    label: 'Este mês',    IconRight: FiChevronDown },
  { value: 'mes_anterior', label: 'Mês anterior', IconRight: null },
];

function Main() {
  const { user, setPage } = useContext(AccountContext);
  const vendedor_id = user?.id_vendedor;

  useEffect(() => { setPage('Extrato financeiro'); }, []); 

  const [allContratos, setAllContratos] = useState([]);
  const [salesStats,   setSalesStats]   = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [periodo,      setPeriodo]      = useState('mes_atual');
  const [tabAtiva,     setTabAtiva]     = useState('todos');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resC, resS] = await Promise.all([
        api(`${ENDPOINT}/entrepreneur/contratos-vendidos`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vendedor_id, status: null }),
        }),
        api(`${ENDPOINT}/entrepreneur/sales`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vendedor_id }),
        }),
      ]);
      const dC = await resC.json();
      const dS = await resS.json();
      setAllContratos(dC?.success ? dC.data : []);
      setSalesStats(dS?.success ? dS.data : null);
    } catch (e) {
      console.log('ExtratoFinanceiro fetchData error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const contratosPeriodo = useMemo(
    () => filterByPeriod(allContratos, periodo),
    [allContratos, periodo],
  );

  const recebido    = useMemo(() => contratosPeriodo.filter(isPago).reduce((s, c) => s + (Number(c.valor) || 0), 0), [contratosPeriodo]);
  const qtdRecebido = useMemo(() => contratosPeriodo.filter(isPago).length, [contratosPeriodo]);
  const aReceber    = salesStats?.previsto_receber || 0;
  const taxas       = recebido * 0.04;
  const liquidoPrev = recebido + aReceber - taxas;

  const bars = useMemo(() => calcBars(contratosPeriodo), [contratosPeriodo]);

  const transacoesFiltradas = useMemo(() => {
    if (tabAtiva === 'entradas') return contratosPeriodo.filter(isPago);
    if (tabAtiva === 'pendentes') return contratosPeriodo.filter((c) => !isPago(c));
    return contratosPeriodo;
  }, [contratosPeriodo, tabAtiva]);

  const grupos = useMemo(() => agruparPorDia(transacoesFiltradas), [transacoesFiltradas]);

  const mesAtual = mesLabel(periodo);

  return (
    <Box bg="#F4F7F9" minH="100vh" display="flex" flexDirection="column">
      <ChildHeader />

      <Box overflowY="auto" flex={1} pb="100px">
        <VStack spacing="20px" px="20px" pt="16px" pb="24px" w="100%" alignItems="flex-start">

          <HStack spacing="8px" w="100%" overflowX="auto" pb="2px">
            {PERIODOS.map((p) => {
              const ativo = periodo === p.value;
              return (
                <HStack
                  key={p.value}
                  as="button"
                  spacing="6px"
                  px="14px" py="8px"
                  borderRadius="20px"
                  bg={ativo ? '#1F7D75' : 'white'}
                  border="1.5px solid"
                  borderColor={ativo ? '#1F7D75' : '#DDE3EA'}
                  flexShrink={0}
                  cursor="pointer"
                  _active={{ opacity: 0.8 }}
                  onClick={() => { if (p.value !== 'filtros') setPeriodo(p.value); }}
                >
                  {p.IconLeft && <p.IconLeft color={ativo ? 'white' : '#5B6B7A'} size={12} />}
                  <Text fontSize="12px" fontWeight={ativo ? '600' : '500'} color={ativo ? 'white' : '#5B6B7A'}>
                    {p.label}
                  </Text>
                  {p.IconRight && <p.IconRight color={ativo ? 'white' : '#5B6B7A'} size={12} />}
                </HStack>
              );
            })}
          </HStack>

          {loading ? (
            <Box w="100%" pt="40px">
              <Loading />
            </Box>
          ) : (
            <>
              <Box
                w="100%" borderRadius="22px" p="20px"
                bgGradient="linear(200deg, #1F7D75, #2DAA9E)"
              >
                <Flex justifyContent="space-between" alignItems="flex-start" mb="16px">
                  <VStack spacing="6px" alignItems="flex-start">
                    <Text color="rgba(255,255,255,0.8)" fontSize="13px" fontWeight="500">
                      Recebido em {mesAtual}
                    </Text>
                    <HStack spacing="6px" alignItems="flex-end">
                      <Text color="rgba(255,255,255,0.8)" fontSize="16px" fontWeight="600" mb="4px">
                        R$
                      </Text>
                      <Text color="white" fontSize="32px" fontWeight="700" lineHeight="1">
                        {formatBRL(recebido)}
                      </Text>
                    </HStack>
                  </VStack>

                  <HStack
                    spacing="4px" px="10px" py="6px"
                    borderRadius="14px" bg="rgba(255,255,255,0.2)"
                    flexShrink={0}
                  >
                    <FiTrendingUp color="white" size={12} />
                    <Text color="white" fontSize="11px" fontWeight="600">
                      {qtdRecebido} {qtdRecebido === 1 ? 'contrato' : 'contratos'}
                    </Text>
                  </HStack>
                </Flex>

                <MiniChart bars={bars} />
              </Box>

              <Box w="100%" bg="white" borderRadius="18px" border="1px solid #EEF1F5" p="6px">

                <Box px="6px" py="4px">
                  <SummaryRow
                    icon={FiArrowDownLeft}
                    iconColor="#16A34A" iconBg="#DCFCE7"
                    title="Já recebido"
                    subtitle={`${qtdRecebido} transaç${qtdRecebido === 1 ? 'ão' : 'ões'} compensada${qtdRecebido === 1 ? '' : 's'}`}
                    value={`R$ ${formatBRL(recebido)}`}
                    valueColor="#16A34A"
                  />
                </Box>
                <Box h="1px" bg="#EEF1F5" />

                <Box px="6px" py="4px">
                  <SummaryRow
                    icon={FiClock}
                    iconColor="#D97706" iconBg="#FEF3C7"
                    title="A receber"
                    subtitle="Liberado conforme compensação do pagamento"
                    value={`R$ ${formatBRL(aReceber)}`}
                    valueColor="#D97706"
                  />
                </Box>
                <Box h="1px" bg="#EEF1F5" />

                <Box px="6px" py="4px">
                  <SummaryRow
                    icon={FiPercent}
                    iconColor="#EA580C" iconBg="#FFEDD5"
                    title="Taxas do mês"
                    subtitle="Plataforma 4% + tributos (estimado)"
                    value={`-R$ ${formatBRL(taxas)}`}
                    valueColor="#EA580C"
                  />
                </Box>
                <Box h="1px" bg="#EEF1F5" />

                <Box bg="#E8F5F3" borderRadius="14px" p="12px" mt="4px">
                  <SummaryRow
                    icon={FiDollarSign}
                    iconColor="white" iconBg="#1F7D75"
                    title="Total líquido previsto"
                    subtitle="Comissões pagas + a receber - taxas"
                    value={`R$ ${formatBRL(liquidoPrev)}`}
                    valueColor="#1F7D75"
                    bold
                    valueSize="16px"
                  />
                </Box>
              </Box>

              <HStack
                spacing="12px" p="14px" w="100%"
                bg="#EFF6FF" borderRadius="16px"
                alignItems="flex-start"
              >
                <Flex
                  w="32px" h="32px" minW="32px"
                  bg="#3B82F6" borderRadius="10px"
                  alignItems="center" justifyContent="center"
                >
                  <FiInfo color="white" size={18} />
                </Flex>
                <VStack spacing="4px" alignItems="flex-start">
                  <Text color="#0F1B2A" fontSize="12px" fontWeight="700">
                    Quando o valor cai na sua conta?
                  </Text>
                  <Text color="#5B6B7A" fontSize="11px" fontWeight="500" lineHeight="1.4">
                    As comissões são transferidas automaticamente conforme a compensação da forma de pagamento usada pelo cliente
                    {' '}(Pix: na hora • Cartão: D+30).
                  </Text>
                </VStack>
              </HStack>

              <VStack spacing="14px" w="100%">

                <Flex justifyContent="space-between" alignItems="center" w="100%">
                  <Text color="#0F1B2A" fontSize="17px" fontWeight="700">Movimentações</Text>

                  <HStack spacing="0px" bg="#EEF1F5" borderRadius="10px" p="3px">
                    {[
                      { value: 'todos',     label: 'Todos' },
                      { value: 'entradas',  label: 'Entradas' },
                      { value: 'pendentes', label: 'Pendentes' },
                    ].map((tab) => (
                      <Box
                        key={tab.value}
                        as="button"
                        px="10px" py="5px"
                        borderRadius="8px"
                        bg={tabAtiva === tab.value ? 'white' : 'transparent'}
                        border={tabAtiva === tab.value ? '1px solid #DDE3EA' : 'none'}
                        cursor="pointer"
                        onClick={() => setTabAtiva(tab.value)}
                      >
                        <Text
                          fontSize="11px"
                          fontWeight={tabAtiva === tab.value ? '600' : '500'}
                          color={tabAtiva === tab.value ? '#0F1B2A' : '#5B6B7A'}
                          whiteSpace="nowrap"
                        >
                          {tab.label}
                        </Text>
                      </Box>
                    ))}
                  </HStack>
                </Flex>

                {grupos.length === 0 ? (
                  <Text color="#9AA5B1" textAlign="center" pt="20px" fontSize="14px" w="100%">
                    Nenhuma movimentação no período.
                  </Text>
                ) : (
                  grupos.map((grupo) => (
                    <Box key={grupo.key} w="100%">
                      <Text
                        color="#9AA5B1" fontSize="11px" fontWeight="600"
                        letterSpacing="0.5px" mb="8px"
                      >
                        {grupo.label}
                      </Text>
                      <Box bg="white" borderRadius="18px" border="1px solid #EEF1F5" p="6px">
                        {grupo.items.map((item, i) => (
                          <TransactionItem
                            key={i}
                            item={item}
                            isLast={i === grupo.items.length - 1}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))
                )}
              </VStack>
            </>
          )}
        </VStack>
      </Box>

      <BottomNavBar name="empreendedor" />
    </Box>
  );
}

export default Main;
